/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { closest, onceAnimationEnd } from 'volta/utils/dom-utils';

import type { ComponentLike } from '@glint/template';
import type { BvModalBodySignature } from 'volta/components/bv-modal/body';
import type { BvModalFooterSignature } from 'volta/components/bv-modal/footer';
import type { BvModalSectionSignature } from 'volta/components/bv-modal/section';
import type { BvModalSectionHeaderSignature } from 'volta/components/bv-modal/section-header';
import type { BvModalHeaderSignature } from './header';

type ISize = 'xs' | 'sm' | 'lg' | 'xl' | 'xxl' | 'search';

interface BvModalSignature {
  Args: {
    subtitle?: string;
    title?: string;
    closeLabel?: string;
    forceDesktopStyle?: boolean;
    dialog?: boolean;
    backdropClassNames?: string;
    hiddenBackdrop?: boolean;
    onClickOverlay?: () => void;
    onClose?: () => void;
    onInsert?: (context: TContext) => void;
    size?: ISize;
    sheet?: boolean;
    sheetSide?: 'left' | 'right';
    /**
     * Shows the close button in the header
     * or on the side of a sheet modal
     * @default true;
     **/
    showDismiss?: boolean;
    translucentBackdrop?: boolean;
    isLoading?: boolean;
    isShowing?: boolean;
    escapable?: boolean;
    closingDelay?: number;
    isFocusTrapActive?: boolean;
    shouldSelfFocus?: boolean;
    isFocusTrapPaused?: boolean;
    focusTrapOptions?: {
      clickOutsideDeactivates: boolean;
      initialFocus: boolean;
      escapeDeactivates: boolean;
    };
    belowAppHeader?: boolean;
    initialFocusElement?:
      | HTMLElement
      | SVGElement
      | string
      | false
      | undefined
      | (() => HTMLElement | SVGElement | string | false | undefined);
  };
  Blocks: {
    default: [
      {
        Header: ComponentLike<BvModalHeaderSignature>;
        Body: ComponentLike<BvModalBodySignature>;
        Footer: ComponentLike<BvModalFooterSignature>;
        Section: ComponentLike<BvModalSectionSignature>;
        SectionHeader: ComponentLike<BvModalSectionHeaderSignature>;
      }
    ];
  };
  Element: HTMLDivElement;
}

type TContext = {
  isShowing: boolean;
  isWarning: boolean;
  isLoading: boolean;
  close: () => void;
};

export default class BvModalComponent extends Component<BvModalSignature> {
  modalEl!: HTMLElement;
  @tracked isShowing = true;
  @tracked isWarning = false;

  get additionalFocusElements() {
    return ['#ember-basic-dropdown-wormhole', '.ember-basic-dropdown-content'].filter(
      (e) => document.querySelector(e) != null
    );
  }

  get context(): TContext {
    return {
      isShowing: this.isShowing,
      isWarning: this.isWarning,
      isLoading: this.args.isLoading ?? false,
      close: () => {
        return this.close.perform();
      }
    };
  }

  get escapable() {
    return this.args.escapable ?? true;
  }

  @action
  attemptEscape(event?: KeyboardEvent | PointerEvent) {
    if (event && this.eventIsFromNestedDropdownOrModal(event)) {
      return;
    }

    if (this.escapable) {
      this.close.perform();
    } else {
      this.warn.perform();
    }
  }

  insert = task(async (el: HTMLElement) => {
    this.modalEl = el;
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-showing');
    this.args.onInsert?.(this.context);
  });

  close = task(async () => {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-hiding');
    this.isShowing = false;
    this.args.onClose?.();
  });

  warn = task(async () => {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    if (!this.isShowing) {
      return;
    }

    this.isWarning = true;
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-warning');
    this.isWarning = false;
  });

  private eventIsFromNestedDropdownOrModal(event: KeyboardEvent | PointerEvent) {
    const maybeModalEl = closest(event.target as HTMLElement, '.bv-modal');
    return (
      closest(event.target as HTMLElement, '.ember-basic-dropdown-trigger[aria-expanded="true"]') ||
      closest(event.target as HTMLElement, '.ember-basic-dropdown-content') ||
      (maybeModalEl && maybeModalEl != this.modalEl)
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvModal: typeof BvModalComponent;
    'bv-modal': typeof BvModalComponent;
  }
}
